import React from 'react';
import moment from 'moment-timezone';
import ContextualLink from '@magoosh/gre/app/contextual_link';
import { paths } from 'config/path_helpers';
import { pluralize } from 'utilities/pluralize';

import { useModalContext } from '@magoosh/context/modal_context';
import { useUserContext } from '@magoosh/context/user_context';
// TODO - put this shared data in a better spot
import { ExamSectionNotes } from '@magoosh/gre/adaptive_prep_v2/pages/study_plan/types';

import { OverlayTrigger, Popover } from 'react-bootstrap';
import { DefaultButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import AboutTimedStudyTracker from '../about_timed_study_tracker';
import DonutChart from '../../lib/donut_chart';
import LiveClassesWidget from '../live_classes_widget';
import { TrialDaysBadge, PremiumBadge } from '@magoosh/lib/info_badges';

import NotesGray from '../../assets/notes_icon_zero.svg';
import style from './style.module.scss';


interface Props {
  hoursStudiedThisWeek: number;
  isLoading: boolean;
  notes: ExamSectionNotes[];
  recommendedWeeklyHours: number;
}

export const SideBar: React.FC<Props> = (props) => {
  const { setModal } = useModalContext();

  const {
    hoursStudiedThisWeek,
    isLoading,
    notes,
    recommendedWeeklyHours
  } = props;

  const { adaptivePrepData, testDate } = useUserContext();

  const remainingDays = moment(testDate).endOf('day').diff(moment().startOf('day'), 'days');
  const trialDaysLeft = moment(adaptivePrepData.trialExpiresAt)
    .endOf('day')
    .diff(moment().startOf('day'), 'days');
  
  const handleTimeTrackerLearnMoreClicked = () => {
    setModal({
      header: 'About the Timed Study Tracker',
      body: <AboutTimedStudyTracker handleClose={() => setModal(null)} />
    });
  };

  return (
    <div className={style.sideBar}>
      {adaptivePrepData.isTrial && (
        <div className={style.trialSection}>
          <div className="d-flex justify-content-between align-items-center">
            <h4>Free Trial</h4>
            {adaptivePrepData.trialExpiresAt && trialDaysLeft < 8 && (
              <TrialDaysBadge daysLeft={trialDaysLeft} />
            )}
          </div>
          {adaptivePrepData.trialExpiresAt && trialDaysLeft < 8 && (
            <span>Your trial ends on {moment(adaptivePrepData.trialExpiresAt).format('MMMM D, YYYY')}. </span>
          )}
          <span>Upgrade anytime for access to practice tests, more content, and future live classes.</span>
          <div className="u-margin-T-xs">
            <DefaultButton
              size="md"
              block={true}
              className={style.purpleButton}
              href={paths.plans()}
              target={'_blank'}>
              Upgrade
            </DefaultButton>
          </div>
        </div>
      )}
      <div className={style.section}>
        <h4>
          <strong>GRE Test Date</strong>{' '}
          <OverlayTrigger
            trigger={['hover']}
            placement="bottom"
            rootClose={true}
            overlay={
              <Popover id="assessment-card-popover">
                We use your test date to help you pace your studies, including by letting you know when
                it&apos;s a good time for you to take your next full-length practice test.
              </Popover>
            }>
            <small>
              <FontAwesomeIcon icon={'info-circle'} />
            </small>
          </OverlayTrigger>
        </h4>
        {testDate ? (
          <div className={style.sectionItem}>
            <p className="u-margin-B-n">
              {moment(testDate).format('MMMM DD, YYYY')} <br />
              {remainingDays > 0 && <small>{pluralize(remainingDays, 'Day')} Remaining</small>}
            </p>
            <ContextualLink style={{ color: style.brandSecondary }} href={paths.profileEdit()}>
              <strong>Change</strong>
            </ContextualLink>
          </div>
        ) : isLoading ? (
          <div className="text-center">
            <FontAwesomeIcon icon="spinner" className="fa-pulse fa-2x" />
          </div>
        ) : (
          <div className={style.sectionItem}>
            <p>No test date on file</p>
            <ContextualLink style={{ color: style.brandSecondary }} href={paths.profileEdit()}>
              <strong>Add Test Date</strong>
            </ContextualLink>
          </div>
        )}
      </div>

      <div className={style.section}>
        <h4>
          <strong>Timed Study Tracker</strong>{' '}
        </h4>
        <p>We recommend at least {pluralize(recommendedWeeklyHours, 'hour')} per week.</p>
        <ContextualLink onClick={handleTimeTrackerLearnMoreClicked}>Learn More</ContextualLink>
        <DonutChart progress={(hoursStudiedThisWeek / recommendedWeeklyHours) * 100} variant="primary">
          {hoursStudiedThisWeek > 0 ? (
            <h3>{pluralize(hoursStudiedThisWeek, 'Hour')}</h3>
          ) : (
            <p style={{ color: style.grayLight }}>No Progress Yet</p>
          )}
          <p>Week of {moment().startOf('isoWeek').format('M/D')}</p>
        </DonutChart>

        <ContextualLink href={paths.performance()}>See All Performance</ContextualLink>
      </div>
      <LiveClassesWidget />
      <div className={style.section}>
        <h4>
          <strong>Supplemental Practice</strong>
        </h4>
        <div className={style.sectionItem}>
          <p>
            Custom Practice{adaptivePrepData.isTrial && <PremiumBadge className={'u-margin-H-xs'} />} <br />
            <small>Practice with options to customize subject mix and difficulty.</small>
          </p>
          <ContextualLink style={{ color: style.brandSecondary }} href={paths.startPractice}>
            <strong>{adaptivePrepData.isTrial ? 'Preview' : 'Start'}</strong>
          </ContextualLink>
        </div>
        <div className={style.sectionItem}>
          <p className="u-margin-B-n">
            Vocabulary Flashcards <br />
            <small>Build your vocabulary using our web-based flashcards.</small>
          </p>
          <ContextualLink style={{ color: style.brandSecondary }} href={paths.flashcards('vocabulary')} target="_blank">
            <strong>Start</strong>
          </ContextualLink>
        </div>
      </div>
      <div className={style.section}>
        <div>
          <h4>
            <strong>Your Notes</strong>
          </h4>
          {notes && notes.every((note) => note.noteCount === 0) ? (
            <div className="text-center">
              <img src={NotesGray} />
              <h5>No Notes Yet</h5>
              <p>You may access notes you take on any lesson video or question right here.</p>
            </div>
          ) : (
            <div>
              {notes && notes.map((note) => {
                return (
                  <div key={note.sectionSlug}>
                    <ContextualLink href={paths.examSectionNotes(note.sectionSlug)}>
                      View {note.noteCount} {note.sectionName} note{note.noteCount == 1 ? '' : 's'}
                    </ContextualLink>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className={style.section}>
        <div>
          <h4>
            <strong>Resources</strong>
          </h4>

          <ul className="list-unstyled">
            <li>
              <ContextualLink href={paths.topicOverview()}>Topic Overview</ContextualLink>
            </li>
            <li>
              <ContextualLink rel="zenbox" className="zendesk-button">
                Get Academic Help
              </ContextualLink>
              {adaptivePrepData.isTrial ? <p>Limited availability during free trial</p> : <br />}
            </li>
            <li>
              <ContextualLink href={paths.blog('gre')} target="_blank">
                Magoosh GRE Blog
              </ContextualLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SideBar;
